import { X, AlertCircle } from 'lucide-react';
import { useFilteredSchemaGraphData } from '../schema-graph/graphLoader';
import SchemaGraphPage from '../schema-graph/SchemaGraphPage';

interface ModuleGraphModalProps {
  isOpen: boolean;
  moduleName: string | null;
  onClose: () => void;
}

export function ModuleGraphModal({ isOpen, moduleName, onClose }: ModuleGraphModalProps) {
  const { graphData, documents, loading, error } = useFilteredSchemaGraphData(isOpen ? moduleName : null);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-h-[90vh] max-w-[95vw] flex flex-col" style={{ height: '90vh' }}>
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Module: {moduleName}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                View tables and relationships for this module
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-100 rounded-lg transition-colors text-gray-500 hover:text-gray-700"
              aria-label="Close modal"
              title="Close (Esc)"
            >
              <X size={24} />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-hidden flex flex-col bg-white" style={{ display: 'flex', width: '100%' }}>
            {loading && (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="inline-flex animate-spin mb-4">
                    <div className="w-8 h-8 border-4 border-gray-200 border-t-blue-500 rounded-full" />
                  </div>
                  <p className="text-gray-600 text-sm">Loading module graph...</p>
                </div>
              </div>
            )}

            {error && (
              <div className="flex items-center justify-center h-full">
                <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold text-red-900 mb-1">
                        Failed to load module
                      </h3>
                      <p className="text-red-700 text-sm">{error}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {!loading && !error && graphData && (
              <div style={{ width: '100%', height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <SchemaGraphPage
                  graphData={graphData}
                  documents={documents}
                  isModal={true}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
